import { useState, type FormEvent } from "react";
import type { VisitNoteInput } from "../lib/apiClient";

interface VisitNoteFormProps {
  initialValue?: VisitNoteInput;
  submitLabel: string;
  onSubmit: (input: VisitNoteInput) => Promise<void>;
}

const empty: VisitNoteInput = {
  patientName: "",
  doctorName: "",
  visitDate: new Date().toISOString().slice(0, 16),
  diagnosis: "",
  notes: "",
};

export function VisitNoteForm({
  initialValue = empty,
  submitLabel,
  onSubmit,
}: VisitNoteFormProps) {
  const [form, setForm] = useState<VisitNoteInput>(initialValue);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        ...form,
        visitDate: new Date(form.visitDate).toISOString(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="visit-note-form">
      <label>
        Patient name
        <input
          required
          value={form.patientName}
          onChange={(e) => setForm({ ...form, patientName: e.target.value })}
        />
      </label>
      <label>
        Doctor name
        <input
          required
          value={form.doctorName}
          onChange={(e) => setForm({ ...form, doctorName: e.target.value })}
        />
      </label>
      <label>
        Visit date
        <input
          required
          type="datetime-local"
          value={form.visitDate.slice(0, 16)}
          onChange={(e) => setForm({ ...form, visitDate: e.target.value })}
        />
      </label>
      <label>
        Diagnosis
        <input
          value={form.diagnosis ?? ""}
          onChange={(e) => setForm({ ...form, diagnosis: e.target.value })}
        />
      </label>
      <label>
        Notes
        <textarea
          required
          rows={5}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </label>
      {error && <p role="alert">{error}</p>}
      <button type="submit" disabled={submitting}>
        {submitting ? "Saving…" : submitLabel}
      </button>
    </form>
  );
}
